import { Maybe } from '@codegrade/maybe';
import { assert, defaultdict, object, sort } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/** @returns The initial state of the sections' store (empty). */
const makeInitialState = () => ({
    loaders: {},
    courseSections: {},
});
const moduleBuilder = storeBuilder.module('sections', makeInitialState());
/** The implementation of the sections' store. */
export var SectionsStore;
(function (SectionsStore) {
    /** Commit the sections for a course. */
    SectionsStore.commitCourseSections = moduleBuilder.commit((state, { courseId, sections, }) => {
        // This would break the store otherwise, but the check is not worth it in production.
        if (!IS_PRODUCTION) {
            assert.AssertionError.assert(sections.every(section => section.courseId === courseId));
        }
        // We do not sort the sections here on name, because in the normal case,
        // we still want the sorting to adhere to the created_at time.
        utils.vueSet(state.courseSections, courseId, sections);
    }, 'commitCourseSections');
    const removeLoader = moduleBuilder.commit((state, { courseId }) => {
        utils.vueDelete(state.loaders, courseId);
    }, 'removeLoader');
    const addLoader = moduleBuilder.commit((state, { courseId, loader, }) => {
        utils.vueSet(state.loaders, courseId, loader);
    }, 'commitAllCourseSectionsLoader');
    /**
     * Retrieve a function that returns all the sections for a user in a course.
     *
     * @param state The state of the sections store.
     * @returns A function retrieving the sections for a user in a specific course.
     */
    // eslint-disable-next-line no-underscore-dangle
    function _getUserSections(state) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const mapping = defaultdict.defaultdict((_courseId) => 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        defaultdict.defaultdict((_userId) => []));
        Object.values(state.courseSections).forEach(sections => {
            // First sort the sections in this couse by name, such that
            // we automatically get insertion sort order when setting the user sections.
            sort.sortBy(sections, section => [section.name, section.id]).forEach(section => {
                const curSubmap = mapping[section.courseId];
                section.memberIds.ifJust(memberIds => memberIds.forEach(memberId => {
                    curSubmap[memberId].push(section);
                }));
            });
        });
        return (courseId, userId) => mapping[courseId][userId];
    }
    /**
     * Get a function that allows you to retrieve the sorted sections for a user in a specific course.
     *
     * The sorting is done by name of the sections.
     */
    SectionsStore.getUserSections = moduleBuilder.read(_getUserSections, 'getUserSections');
    /**
     * Retrieve all the sections for the given course.
     *
     * @param state The state of the sections store.
     * @returns A function retrieving all the section if it exists.
     */
    // eslint-disable-next-line no-underscore-dangle
    function _getAllCourseSections(state) {
        return object.getItem(state.courseSections);
    }
    /** Generates a function retrieving all the sections for the given course id. */
    SectionsStore.getAllCourseSections = moduleBuilder.read(_getAllCourseSections, 'getAllCourseSections');
    /** Load all the sections of a course. */
    SectionsStore.loadCourseSections = moduleBuilder.dispatch(({ state }, { courseId, force = false, }) => utils.loadApiWithMaybeForce({
        addLoader: loader => addLoader({ courseId, loader }),
        force,
        value: Maybe.fromNullable(state.courseSections[courseId]),
        load: async () => {
            const result = await client.p.course.getSections({ courseId });
            const { cgResult } = result;
            SectionsStore.commitCourseSections({
                courseId,
                sections: Object.freeze(cgResult.map(x => x)),
            });
            return result;
        },
        loader: Maybe.fromNullable(state.loaders[courseId]),
        removeLoader: () => removeLoader({ courseId }),
    }), 'loadCourseSection');
    const updateCourseSection = moduleBuilder.dispatch(async ({ state }, { section }) => {
        await SectionsStore.loadCourseSections({ courseId: section.courseId });
        Maybe.fromNullable(state.courseSections[section.courseId]).ifJust(sections => {
            SectionsStore.commitCourseSections({
                courseId: section.courseId,
                sections: sections
                    .filter(oldSection => oldSection.id !== section.id)
                    .concat([section]),
            });
        });
    }, 'updateCourseSection');
    /** Creates a new section adds it to the store. */
    SectionsStore.createCourseSection = moduleBuilder.dispatch(async (_, payload) => {
        await SectionsStore.loadCourseSections({ courseId: payload.courseId });
        const resp = await client.p.course.createSection(payload);
        return resp.addCallbacks({
            onAfterSuccess: () => updateCourseSection({ section: resp.cgResult }),
        });
    }, 'createCourseSection');
    /** Deletes the section from the store. */
    SectionsStore.deleteCourseSection = moduleBuilder.dispatch(async ({ state }, { courseId, sectionId, }) => {
        const response = await client.p.section.delete({ sectionId });
        return response.addCallbacks({
            onAfterSuccess: () => {
                object.getItem(state.courseSections, courseId).ifJust(existing => {
                    SectionsStore.commitCourseSections({
                        courseId,
                        sections: existing.filter(section => section.id !== sectionId),
                    });
                });
            },
        });
    }, 'deleteCourseSection');
    /**
     * Update a section in the store with the data from a response.
     *
     * @param resp The response to update the store with.
     * @returns A new response with a callback added to update the store.
     */
    const updateSectionWithResponse = (resp) => resp.addCallbacks({
        onAfterSuccess: () => updateCourseSection({ section: resp.cgResult }),
    });
    /** Patche a section. */
    SectionsStore.patchCourseSection = moduleBuilder.dispatch(async (_, { sectionId, requestBody, }) => {
        const response = await client.p.section.patch({ sectionId, requestBody });
        return updateSectionWithResponse(response);
    }, 'patchCourseSection');
    /** Adds users to a section. */
    SectionsStore.addUsersToCourseSection = moduleBuilder.dispatch(async (_, { sectionId, requestBody, }) => {
        const response = await client.p.section.addUsers({ sectionId, requestBody });
        return updateSectionWithResponse(response);
    }, 'addUsersToCourseSection');
    /** Removes a user from a section. */
    SectionsStore.removeUserFromCourseSection = moduleBuilder.dispatch(async (_, { sectionId, userId, }) => {
        const response = await client.p.section.deleteUser({ sectionId, userId });
        return updateSectionWithResponse(response);
    }, 'removeUserFromCourseSection');
    const commitClearAll = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearAll');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        commitClearAll();
    });
})(SectionsStore || (SectionsStore = {}));
